import React, { useState } from 'react';
import { useStore } from '../store/store';
import api from '../services/api';
import { Flex, TextField, Button } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiPencil, mdiCheck, mdiClose, mdiDelete, mdiChat } from '@mdi/js';

// Format date to "Month Day(th/st/nd/rd)" format
const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    
    const month = months[date.getMonth()];
    const day = date.getDate();
    
    // Add ordinal suffix to day
    let suffix = 'th';
    if (day % 10 === 1 && day !== 11) {
        suffix = 'st';
    } else if (day % 10 === 2 && day !== 12) {
        suffix = 'nd';
    } else if (day % 10 === 3 && day !== 13) {
        suffix = 'rd';
    }
    
    return `${month} ${day}${suffix}`;
};

// Separate component for individual chat items
const ChatItem = ({ setChat, selectedChatId, chat, onDelete, onEdit }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newTitle, setNewTitle] = useState(chat.title);
    
    // Format date nicely
    const chatDate = chat.created_at ? formatDate(chat.created_at) : 'Unknown date';
    
    // Delete handler
    const handleDelete = (e) => {
        e.stopPropagation(); // Prevent triggering any parent click events
        onDelete(chat.id);
    };
    
    // Edit handlers
    const handleEdit = (e) => {
        e.stopPropagation();
        setIsEditing(true);
    };
    
    const handleSave = (e) => {
        e.stopPropagation();
        if (newTitle.trim() !== '') {
            onEdit(chat.id, newTitle);
            setIsEditing(false);
        }
    };
    
    const handleCancel = (e) => {
        e.stopPropagation();
        setNewTitle(chat.title); // Reset to original title
        setIsEditing(false);
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave(e);
        }
    };
    
    return (
        <div className="sidebar-chat-item" onClick={() => {
            selectedChatId === chat.id ? setChat(null) : setChat(chat);
            }}>
            {isEditing ? (
                <>
                    <Flex>
                        <TextField.Root 
                        value={newTitle}
                        type="text"
                        style={{width: "300px"}} 
                        className="sidebar-form-textInput" 
                        variant="soft" 
                        autoFocus
                        onChange={(e) => setNewTitle(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={handleKeyDown}
                        />
                        <div className={"sidebar-chat-item-edit-button-group"}>
                            <button 
                                className="sidebar-chat-item-save"
                                onClick={handleSave}
                                aria-label="Save title"
                            >
                                <Icon style={{transform: "scale(0.7)", marginTop: "-1px", marginLeft: "-5px"}} path={mdiCheck} size={1} />

                            </button>
                            <button 
                                className="sidebar-chat-item-cancel"
                                onClick={handleCancel}
                                aria-label="Cancel edit"
                            >
                                <Icon style={{transform: "scale(0.7)", marginTop: "-1px", marginLeft: "-5px"}} path={mdiClose} size={1} />
                            </button>
                        </div>
                    </Flex>
                </>
            ) : (
                <>
                    <Flex>
                        <div className="sidebar-chat-item-content">{chat.title}</div>
                        <button 
                            className="sidebar-chat-item-edit"
                            onClick={handleEdit}
                            aria-label="Edit chat title"
                        >
                            <Icon style={{transform: "scale(0.7)", marginTop: "-1px", marginLeft: "-5px"}} path={mdiPencil} size={1} />
                        </button>
                    </Flex>
                    <button 
                        className="sidebar-chat-item-delete"
                        onClick={handleDelete}
                        aria-label="Delete chat"
                    >
                        <Icon style={{transform: "scale(0.7)", marginTop: "-1px", marginLeft: "-5px"}} path={mdiDelete} size={1} />
                    </button>
                </>
            )}
            <Flex style={{marginLeft: '-5px'}} gap={"4"}>
                <div className="sidebar-chat-item-date">{chatDate}</div>
                <Flex>
                    <Icon path={mdiChat} size={1} style={{transform: 'scale(0.7)', 'color': '#ffffff8a'}}/>
                    <div className="sidebar-chat-item-length">{chat.messages.length - 1}</div>
                </Flex>
            </Flex>
        </div>
    );
};

const SidebarChat = (props) => {
    const [state, actions] = useStore();
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    
    const handleDeleteChat = (chatId) => {
        api.deleteChat(chatId)
            .then(() => {
                const updatedChats = state.currentNovel?.chats?.filter(chat => chat.id !== chatId);
                actions.setCurrentNovel({ ...state.currentNovel, chats: updatedChats });
            })
            .catch(error => {
                console.error('Error deleting chat:', error);
            });
    };
    
    const handleEditChat = (chatId, newTitle) => {
        api.updateChatTitle(chatId, newTitle)
            .then(() => {
                // Update the chat title in the local state
                const updatedChats = state.currentNovel?.chats?.map(chat => 
                    chat.id === chatId ? { ...chat, title: newTitle } : chat
                );
                actions.setCurrentNovel({ ...state.currentNovel, chats: updatedChats });
            })
            .catch(error => {
                console.error('Error updating chat title:', error);
            });
    };
    
    const handleCreateNewChat = async () => {
        if (!state.currentNovel?.id || isCreatingChat) return;
        
        setIsCreatingChat(true);
        try {
            // Create new empty chat in database
            const response = await api.createEmptyChat(state.currentNovel.id);
            
            if (response && response.success && response.chat) {
                // Update the novel's chats list in the store
                const updatedChats = [...(state.currentNovel.chats || []), response.chat];
                actions.setCurrentNovel({ ...state.currentNovel, chats: updatedChats });
                
                // Set the new chat as selected
                props.setSelectedChat(response.chat);
            } else {
                console.error('Failed to create new chat:', response);
            }
        } catch (error) {
            console.error('Error creating new chat:', error);
        } finally {
            setIsCreatingChat(false);
        }
    };

    const chatList = state.currentNovel?.chats?.map(chat => (
        <ChatItem 
            setChat={props.setSelectedChat}
            selectedChatId={props.selectedChat?.id}
            key={chat.id} 
            chat={chat} 
            onDelete={handleDeleteChat}
            onEdit={handleEditChat}
        />
    ));

    return (
        <div>
            {chatList}
            <div className="sidebar-add-new-button-wrapper">
                {!state.readOnly && <Button 
                    className="sidebar-add-new-chat-button" 
                    onClick={handleCreateNewChat}
                    disabled={isCreatingChat || !state.currentNovel?.id}
                >
                    {isCreatingChat ? 'Creating...' : '+ New Chat'}
                </Button>}
            </div>
        </div>
    );
};

export default SidebarChat;